import { Component, Prop, Vue } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";

import { BAlert, BCard, BCardText } from "@/lib/typed-bootstrap";

import { PublishingStateEnum } from "@/api/common-types";
import { IEnumState, IEnumValueState } from "@/models";
import { v4 as uuid4 } from "uuid";

import FormEnumWidget from "@/components/forms/FormEnumWidget.component";
import { IEnumNameBlurEvent } from "./FormEnumWidget.component";

import plusIconBlack from "@/assets/img/plus-icon.svg";
import styles from "./FormEnumList.component.module.scss";

@Component({
  components: { FormEnumWidget },
})
export default class FormEnumList extends Vue {
  _tsx!: tsx.DeclareProps<{
    value: FormEnumList["value"];
    allowManagingTypes?: FormEnumList["allowManagingTypes"];
  }> &
    tsx.DeclareOnEvents<{
      onSelectEnumType: void;
      onEnumNameChange: IEnumNameBlurEvent;
      onAddEnumTypeValue: void;
    }>;

  @Prop({ required: true }) value!: IEnumState[];
  @Prop({ default: true }) allowManagingTypes!: boolean;

  errorMessage = "";
  plusIconSrc = plusIconBlack;

  get showErrorMessage(): boolean {
    return !!this.errorMessage;
  }

  get selectedEnumType(): IEnumState | null {
    return this.value?.find((enumType) => enumType.selected) ?? null;
  }

  resetSelection() {
    this.value.forEach((enumType) => {
      enumType.selected = false;
    });
  }

  createNewEnumType(): IEnumState {
    const uuid = uuid4();
    const defaultValues: IEnumValueState[] = [
      {
        name: "",
        selected: false,
      },
    ];

    const newEnumType: IEnumState = {
      id: uuid,
      name: "",
      selected: true,
      values: defaultValues,
      state: PublishingStateEnum.INITIALIZED,
    };

    return newEnumType;
  }

  onSelectEnumType(enumType: IEnumState) {
    this.resetSelection();
    enumType.selected = true;
    this.$emit("selectEnumType");
  }

  selectedEnumTypeIsUnique(): boolean | null {
    const isDuplicate = this.value?.some((enumType) => {
      return (
        enumType.id !== this.selectedEnumType?.id &&
        enumType.name === this.selectedEnumType?.name
      );
    });

    this.errorMessage = isDuplicate ? "duplicate value" : "";

    return isDuplicate ? false : null;
  }

  onAddEnumType() {
    try {
      const isEmpty = this.value.length === 0;
      if (isEmpty || this.value[0]?.name?.length > 0) {
        this.resetSelection();
        this.value.unshift(this.createNewEnumType());
      }
    } catch (err) {
      this.errorMessage = (err as Error).message;
    }
  }

  handleDelete() {
    const selectedEnumType = this.selectedEnumType;
    if (selectedEnumType) {
      const index = this.value.findIndex(
        (enumType) => enumType.id === selectedEnumType.id,
      );
      if (index !== -1) {
        this.value.splice(index, 1);
        this.$emit("delete", selectedEnumType.name);
      }
    }
  }

  render() {
    return (
      <div class={styles.enumList}>
        <BAlert show={this.showErrorMessage} variant="danger">
          {this.errorMessage}
        </BAlert>

        <div class={styles.enumTypeCollection} ref="slider">
          {this.allowManagingTypes && (
            <BCard
              bodyClass={styles.ctaAddEnumBody}
              class={["mr-2", styles.slider]}
            >
              <button
                type="button"
                class={styles.ctaAddEnum}
                onClick={(e) => {
                  e.preventDefault();
                  this.onAddEnumType();
                }}
              >
                <img src={this.plusIconSrc} />
                <BCardText class={styles.ctaAddEnumText}>
                  {this.$t("enum_widget_add")}
                </BCardText>
              </button>
            </BCard>
          )}

          {this.value?.map((enumType, index) => (
            <BCard
              key={`enum-type-${index}`}
              bodyClass={[
                styles.enumTypeBody,
                (enumType.selected && styles.isActive) || "",
              ].join(" ")}
              class={["mr-2", styles.slider]}
              onClick={() => this.onSelectEnumType(enumType)}
            >
              <BCardText class={styles.enumTypeText}>
                {enumType.name ? (
                  <div>{enumType.name}</div>
                ) : (
                  <div class={styles.placeholder}>
                    {this.$t("enum_widget_tmp_name")}
                  </div>
                )}
              </BCardText>

              <div class={styles.enumTypeSummary}>
                {enumType.values.length} {this.$t("enum_widget_values")}
              </div>
            </BCard>
          ))}

          <BCard class={[styles.scrollSpacer, "col-3", "col-md-2"]}></BCard>
        </div>

        {this.allowManagingTypes && this.selectedEnumType && (
          <FormEnumWidget
            value={this.selectedEnumType}
            onInput={(val: IEnumState) => {
              this.$emit("enumNameChange", { id: val.id, name: val.name });
            }}
            onDelete={this.handleDelete}
            onAddEnumTypeValue={() => this.$emit("addEnumTypeValue")}
            isUnique={this.selectedEnumTypeIsUnique()}
            key={this.selectedEnumType.id}
            class="p-1"
          />
        )}
      </div>
    );
  }
}
